import React, { useState, useMemo, useEffect, useCallback } from 'react';
import RoomMeasurementCard from './RoomMeasurementCard';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

const STANDARD_ROOMS = [
  'Hall', 'Entré', 'Matsal', 'Vardagsrum', 'Kök',
  'Sovrum', 'Badrum', 'WC', 'Tvättstuga', 'Bastu',
  'Källare', 'Förråd', 'KLK', 'Barnrum'
];

// Matchas som delsträng mot rumsnamnet i gemener, så "Teknikrum 2" och
// "TEKNIK" fångas av samma nyckelord. Bastu räknas som våtrum (Rasmus) —
// den fanns som snabbknapp ovan men hamnade under Rum.
const WET_ROOM_KEYWORDS = [
  'bad', 'wc', 'tvätt', 'toalett', 'dusch', 'spa', 'våtrum', 'teknik', 'bastu', 'sauna',
];

const isWetRoom = (room) => {
  const name = (room.name || '').toLowerCase();
  return WET_ROOM_KEYWORDS.some(w => name.includes(w));
};

const sumRooms = (rooms) => ({
  floorArea: rooms.reduce((s, r) => s + (parseFloat(r.floorArea) || 0), 0),
  wallArea: rooms.reduce((s, r) => s + (r.wallArea || 0), 0),
  perimeter: rooms.reduce((s, r) => s + (r.perimeter || 0), 0),
});

const round1 = (n) => Math.round(n * 10) / 10;

const TotalsGrid = ({ totals, label, accent = 'pink' }) => {
  const colors = {
    pink: 'text-leadme-pink bg-pink-50',
    blue: 'text-blue-600 bg-blue-50',
    green: 'text-green-700 bg-green-50',
  };
  return (
    <div>
      {label && <div className="text-xs font-semibold text-gray-600 mb-2 text-center">{label}</div>}
      <div className="grid grid-cols-3 gap-3 text-center">
        <div className={`p-3 rounded-lg ${colors[accent]}`}>
          <div className="text-xs text-gray-600 mb-1">Golvyta</div>
          <div className="text-xl font-bold">{round1(totals.floorArea)} m²</div>
        </div>
        <div className={`p-3 rounded-lg ${colors[accent]}`}>
          <div className="text-xs text-gray-600 mb-1">Väggyta</div>
          <div className="text-xl font-bold">{round1(totals.wallArea)} m²</div>
        </div>
        <div className={`p-3 rounded-lg ${colors[accent]}`}>
          <div className="text-xs text-gray-600 mb-1">Omkrets</div>
          <div className="text-xl font-bold">{round1(totals.perimeter)} m</div>
        </div>
      </div>
    </div>
  );
};

const RumsmåttSection = ({
  roomMeasurements,
  focusRoomId,
  expanded,
  onToggle,
  onAddRoom,
  onUpdateRoomField,
  onDeleteRoom,
  onToggleRoomEdit,
  onReorderRooms,
  onFocusCleared
}) => {
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  /**
   * Namnet som klassificeringen utgår från — uppdateras först när man lämnat
   * namnfältet, inte vid varje tangenttryck.
   *
   * Tidigare kördes isWetRoom mot det liveuppdaterade namnet. Skrev man "bad"
   * hoppade kortet till Våtrum redan på tredje bokstaven, mitt i skrivandet:
   * markören försvann och det gick inte att förstå vad som hände.
   */
  const [klassnamn, setKlassnamn] = useState({});

  useEffect(() => {
    setKlassnamn(prev => {
      let nytt = null;
      const levande = new Set(roomMeasurements.map(r => String(r.id)));

      roomMeasurements.forEach(r => {
        const nyckel = String(r.id);
        const onskat = prev[nyckel] === undefined
          // Första gången rummet syns gäller dess namn direkt. Ett nyskapat rum
          // är tomt och i redigeringsläge, så det seedas med tom sträng och
          // ligger kvar under Övriga rum tills man skrivit klart.
          ? (r.name || '')
          // Därefter bara när fältet lämnats
          : (r.isEditing ? prev[nyckel] : (r.name || ''));

        if (prev[nyckel] !== onskat) {
          if (!nytt) nytt = { ...prev };
          nytt[nyckel] = onskat;
        }
      });

      Object.keys(prev).forEach(nyckel => {
        if (!levande.has(nyckel)) {
          if (!nytt) nytt = { ...prev };
          delete nytt[nyckel];
        }
      });

      return nytt || prev;
    });
  }, [roomMeasurements]);

  const arVatrum = useCallback((room) => {
    const nyckel = String(room.id);
    const namn = klassnamn[nyckel] !== undefined ? klassnamn[nyckel] : (room.name || '');
    return isWetRoom({ name: namn });
  }, [klassnamn]);

  const toggleSelected = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const { otherRooms, wetRooms, otherTotals, wetTotals, selectedTotals, hasSelection } = useMemo(() => {
    // Manuellt satt ordning gäller före skapandeordningen. Rum som aldrig
    // dragits har sortOrder null och behåller sin plats efter id.
    const sorted = [...roomMeasurements].sort((a, b) => {
      const aOrd = a.sortOrder ?? null;
      const bOrd = b.sortOrder ?? null;
      if (aOrd !== null && bOrd !== null) return aOrd - bOrd;
      if (aOrd !== null) return -1;
      if (bOrd !== null) return 1;
      return a.id - b.id;
    });
    const wet = sorted.filter(arVatrum);
    const other = sorted.filter(r => !arVatrum(r));
    const selected = sorted.filter(r => selectedIds.has(r.id));
    return {
      otherRooms: other,
      wetRooms: wet,
      otherTotals: sumRooms(other),
      wetTotals: sumRooms(wet),
      selectedTotals: sumRooms(selected),
      hasSelection: selected.length > 0,
    };
  }, [roomMeasurements, selectedIds, arVatrum]);

  /**
   * Rummen går att dra om inom sin egen sektion. Mellan sektionerna görs ingen
   * flytt — vilken sektion ett rum hamnar i styrs av namnet, inte av ordningen.
   */
  const onDragEnd = useCallback((result) => {
    if (!result.destination || !onReorderRooms) return;
    if (result.source.droppableId !== result.destination.droppableId) return;
    if (result.source.index === result.destination.index) return;

    const sektion = result.source.droppableId === 'rum-vatrum' ? wetRooms : otherRooms;
    const omflyttad = [...sektion];
    const [flyttad] = omflyttad.splice(result.source.index, 1);
    omflyttad.splice(result.destination.index, 0, flyttad);

    // Skicka HELA den visade ordningen, inte bara den ändrade sektionen, så
    // alla rum får ett sortOrder och listan inte blandar manuell ordning med
    // skapandeordning.
    const helaOrdningen = result.source.droppableId === 'rum-vatrum'
      ? [...otherRooms, ...omflyttad]
      : [...omflyttad, ...wetRooms];

    onReorderRooms(helaOrdningen.map(r => r.id));
  }, [onReorderRooms, wetRooms, otherRooms]);

  const renderRoomGrid = (rooms, droppableId) => (
    <Droppable droppableId={droppableId} direction="horizontal">
      {(provided) => (
        <div
          ref={provided.innerRef}
          {...provided.droppableProps}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
        >
          {rooms.map((room, index) => (
            <Draggable key={room.id} draggableId={String(room.id)} index={index}>
              {(dragProvided, snapshot) => (
                <div
                  ref={dragProvided.innerRef}
                  {...dragProvided.draggableProps}
                  className={snapshot.isDragging ? 'opacity-80' : ''}
                >
                  <RoomMeasurementCard
                    room={room}
                    shouldFocus={focusRoomId === room.id}
                    onFocused={onFocusCleared}
                    onFieldChange={(field, value) => onUpdateRoomField(room.id, field, value)}
                    onDelete={() => onDeleteRoom(room.id)}
                    onToggleEdit={() => onToggleRoomEdit(room.id)}
                    selected={selectedIds.has(room.id)}
                    onToggleSelected={() => toggleSelected(room.id)}
                    dragHandleProps={dragProvided.dragHandleProps}
                  />
                </div>
              )}
            </Draggable>
          ))}
          {provided.placeholder}
        </div>
      )}
    </Droppable>
  );

  return (
    <div className="bg-gray-50 rounded-lg shadow-sm border border-gray-200 mb-6">
      {/* Header */}
      <div
        className="bg-gray-100 px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 cursor-pointer hover:bg-gray-200 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-leadme-gray-dark">📐 Rumsmått</h2>
          <svg
            className={`w-5 h-5 text-gray-500 transition-transform ${expanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Content */}
      {expanded && (
        <div className="p-4 sm:p-6">
          {/* Info box */}
          <div className="text-sm text-gray-600 mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center">
              <svg className="w-4 h-4 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Kalkylhjälp - Beräknar väggyta och omkrets baserat på golvyta och takhöjd. Markera rum för att summera utvalda rum.
            </div>
          </div>

          {/* Knapp för att lägga till nytt rum */}
          <div className="mb-4">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => onAddRoom()}
                className="px-3 py-1 bg-leadme-pink text-white text-xs font-medium rounded-full hover:bg-leadme-pink-dark transition-colors border border-pink-200"
              >
                + Nytt rum
              </button>
              {STANDARD_ROOMS.map(roomName => (
                <button
                  key={roomName}
                  onClick={() => onAddRoom(roomName)}
                  className="px-3 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded-full hover:bg-gray-200 transition-colors border border-gray-300"
                >
                  {roomName}
                </button>
              ))}
            </div>
          </div>

          <DragDropContext onDragEnd={onDragEnd}>
            {/* Övriga rum */}
            {otherRooms.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Rum</h3>
                {renderRoomGrid(otherRooms, 'rum-ovriga')}
              </div>
            )}

            {/* Våtrum */}
            {wetRooms.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Våtrum</h3>
                {renderRoomGrid(wetRooms, 'rum-vatrum')}
              </div>
            )}
          </DragDropContext>

          {/* Sammanfattning */}
          {roomMeasurements.length > 0 && (
            <div className="mt-6 pt-4 border-t border-gray-200 space-y-4">
              {otherRooms.length > 0 && (
                <TotalsGrid totals={otherTotals} label="Total – Rum" accent="pink" />
              )}
              {wetRooms.length > 0 && (
                <TotalsGrid totals={wetTotals} label="Total – Våtrum" accent="blue" />
              )}
              {hasSelection && (
                <div className="pt-3 border-t border-dashed border-gray-300">
                  <TotalsGrid totals={selectedTotals} label="Total – Valda rum" accent="green" />
                  <div className="text-center mt-2">
                    <button
                      onClick={() => setSelectedIds(new Set())}
                      className="text-xs text-gray-500 hover:text-gray-700 underline"
                    >
                      Rensa val
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RumsmåttSection;
